import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MagnifyingGlass, Plus, UploadSimple } from "@phosphor-icons/react";

import { acquisition as acquisitionApi } from "@/api";
import ActaStandardPage from "@/components/layout/ActaStandardPage";

const EMPTY_FORM = {
  firstName: "",
  lastName: "",
  company: "",
  email: "",
  phone: "",
  message: "",
  sourceId: "",
};

function parseCsvLine(line, separator) {
  const values = [];
  let current = "";
  let quoted = false;

  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    if (char === '"') {
      if (quoted && line[index + 1] === '"') {
        current += '"';
        index += 1;
      } else {
        quoted = !quoted;
      }
    } else if (char === separator && !quoted) {
      values.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }
  values.push(current.trim());
  return values;
}

function parseCsv(text) {
  const lines = String(text || "").replace(/\r/g, "").split("\n").filter((line) => line.trim());
  if (lines.length < 2) return [];
  const separator = lines[0].includes(";") ? ";" : ",";
  const headers = parseCsvLine(lines[0], separator).map((value) => value.trim().toLowerCase());

  const aliases = {
    prenom: "firstName",
    prénom: "firstName",
    firstname: "firstName",
    nom: "lastName",
    lastname: "lastName",
    societe: "company",
    société: "company",
    company: "company",
    email: "email",
    "e-mail": "email",
    telephone: "phone",
    téléphone: "phone",
    phone: "phone",
    message: "message",
    besoin: "message",
  };

  return lines.slice(1).map((line) => {
    const cells = parseCsvLine(line, separator);
    return Object.fromEntries(
      headers.map((header, index) => [aliases[header] || header, cells[index] || ""])
    );
  });
}

const STATUS_LABELS = {
  new: "Nouveau",
  to_assign: "À affecter",
  assigned: "Affecté",
};

export default function AcquisitionLeads() {
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get("view") || "all";
  const campaignId = searchParams.get("campaign_id") || "";
  const assignedUserId = searchParams.get("assigned_user_id") || "";
  const fromDate = searchParams.get("from_date") || "";
  const toDate = searchParams.get("to_date") || "";
  const [leads, setLeads] = useState([]);
  const [sources, setSources] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState(["new", "to_assign", "assigned"].includes(searchParams.get("view")) ? searchParams.get("view") : "");
  const [sourceId, setSourceId] = useState(searchParams.get("source_id") || "");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);

  async function load() {
    setLoading(true);
    setError("");
    try {
      const [leadRows, sourceRows] = await Promise.all([
        acquisitionApi.listLeads({ search, status, source_id: sourceId, campaign_id: campaignId, assigned_user_id: assignedUserId, from_date: fromDate, to_date: toDate, opportunity_filter: ["opportunity", "won"].includes(view) ? view : "" }),
        acquisitionApi.listSources(),
      ]);
      setLeads(Array.isArray(leadRows) ? leadRows : []);
      setSources(Array.isArray(sourceRows) ? sourceRows : []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Impossible de charger les leads.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const timeout = window.setTimeout(load, 250);
    return () => window.clearTimeout(timeout);
  }, [search, status, sourceId, campaignId, assignedUserId, fromDate, toDate, view]);

  const sourceMap = useMemo(
    () => Object.fromEntries(sources.map((item) => [item.id, item.name])),
    [sources]
  );

  async function importCsv(event) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!sourceId) {
      setError("Sélectionnez une source avant l’import.");
      return;
    }

    setImporting(true);
    setError("");
    try {
      const text = await file.text();
      const rows = parseCsv(text);
      if (!rows.length) {
        throw new Error("Le fichier CSV ne contient aucune ligne exploitable.");
      }
      const result = await acquisitionApi.importLeads(sourceId, rows);
      if (result?.errors?.length) {
        setError(`${result.created || 0} lead(s) importé(s), ${result.errors.length} ligne(s) en erreur.`);
      }
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Impossible d’importer le fichier.");
    } finally {
      setImporting(false);
    }
  }

  async function submit(event) {
    event.preventDefault();
    setSaving(true);
    setError("");
    try {
      await acquisitionApi.createLead(form);
      setModalOpen(false);
      setForm(EMPTY_FORM);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Impossible de créer le lead.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <ActaStandardPage testId="acquisition-leads">
      <div className="flex min-w-0 flex-wrap justify-end gap-2">
        <div className="flex min-w-0 flex-wrap gap-2">
          <label className={`btn-secondary min-h-[44px] cursor-pointer px-4 ${importing ? "opacity-50" : ""}`}>
            <UploadSimple size={18} /> {importing ? "Import…" : "Importer"}
            <input type="file" accept=".csv,text/csv" className="hidden" disabled={importing} onChange={importCsv} />
          </label>
          <button type="button" className="btn-primary min-h-[44px] px-4" onClick={() => setModalOpen(true)}>
            <Plus size={18} /> Nouveau lead
          </button>
        </div>
      </div>

      {view !== "all" || campaignId || assignedUserId || fromDate || toDate ? <div className="mt-4 flex min-w-0 flex-wrap items-center gap-3 text-sm text-[var(--do-muted)]">Filtres du tableau de bord actifs <button type="button" className="btn-secondary px-3 py-2" onClick={() => { setSearchParams({}); setStatus(""); setSourceId(""); }}>Effacer les filtres</button></div> : null}
      <div className="mt-6 grid min-w-0 grid-cols-[repeat(auto-fit,minmax(min(13rem,100%),1fr))] gap-3">
        <label className="relative min-w-0">
          <MagnifyingGlass className="absolute left-3 top-3.5 text-[var(--do-muted)]" size={18} />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Rechercher un lead"
            className="w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] py-3 pl-10 pr-3 text-sm text-[var(--do-ink)]"
          />
        </label>
        <select value={status} onChange={(event) => setStatus(event.target.value)} className="rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-3 text-sm text-[var(--do-ink)]">
          <option value="">Tous les statuts</option>
          <option value="new">Nouveau</option>
          <option value="to_assign">À affecter</option>
          <option value="assigned">Affecté</option>
        </select>
        <select value={sourceId} onChange={(event) => setSourceId(event.target.value)} className="rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-3 text-sm text-[var(--do-ink)]">
          <option value="">Toutes les sources</option>
          {sources.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
        </select>
      </div>

      {error ? <div className="mt-4 rounded-2xl border border-[var(--do-danger-border)] bg-[var(--do-danger-soft)] p-4 text-sm text-[var(--do-danger)]">{error}</div> : null}

      <div className="mt-5 min-w-0 max-w-full overflow-hidden rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)]">
        <div className="min-w-0 max-w-full overflow-x-auto overscroll-x-contain">
          <table className="w-full min-w-[900px] text-left text-sm">
            <thead className="border-b border-[var(--do-border)] text-xs uppercase tracking-wide text-[var(--do-muted)]">
              <tr>
                <th className="px-4 py-3">Lead</th>
                <th className="px-4 py-3">Coordonnées</th>
                <th className="px-4 py-3">Source</th>
                <th className="px-4 py-3">Campagne</th>
                <th className="px-4 py-3">Statut</th>
                <th className="px-4 py-3">Alerte</th>
                <th className="px-4 py-3">Commercial</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" className="px-4 py-8 text-center text-[var(--do-muted)]">Chargement…</td></tr>
              ) : leads.length === 0 ? (
                <tr><td colSpan="7" className="px-4 py-8 text-center text-[var(--do-muted)]">Aucun lead.</td></tr>
              ) : leads.map((lead) => {
                const source = lead?.provenance?.source;
                const alert = lead?.alerts?.[0];
                return (
                  <tr key={lead.id} className="border-b border-[var(--do-border)] last:border-0 hover:bg-[var(--do-accent-soft)]">
                    <td className="px-4 py-4">
                      <Link className="font-bold text-[var(--do-ink)] hover:text-[var(--do-accent)]" to={`/app/acquisition/leads/${lead.id}`}>
                        {[`${lead.firstName || ""}`, `${lead.lastName || ""}`].join(" ").trim() || lead.company || "Sans nom"}
                      </Link>
                      {lead.company ? <div className="mt-1 text-xs text-[var(--do-muted)]">{lead.company}</div> : null}
                    </td>
                    <td className="px-4 py-4 text-[var(--do-muted)]">
                      <div>{lead.email || "—"}</div><div>{lead.phone || ""}</div>
                    </td>
                    <td className="px-4 py-4">{source?.name || sourceMap[source?.id] || "—"}</td>
                    <td className="px-4 py-4">{lead?.provenance?.campaignName || "—"}</td>
                    <td className="px-4 py-4"><span className="acta-status-badge">{STATUS_LABELS[lead.status] || lead.statusLabel || lead.status}</span></td>
                    <td className="px-4 py-4">{lead.rejected ? "Rejeté" : alert?.label || "—"}</td>
                    <td className="px-4 py-4">{lead.assignedUserName || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {modalOpen ? (
        <div className="fixed inset-0 z-[160] flex items-center justify-center acta-modal-overlay p-4" onClick={() => !saving && setModalOpen(false)}>
          <form className="acta-modal-surface w-full max-w-2xl rounded-3xl bg-[var(--do-bg-solid)] p-6" onSubmit={submit} onClick={(event) => event.stopPropagation()}>
            <h2 className="font-heading text-xl font-black text-[var(--do-ink)]">Nouveau lead</h2>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <input required placeholder="Prénom" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} className="rounded-xl border border-[var(--do-border)] bg-transparent px-3 py-3" />
              <input required placeholder="Nom" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} className="rounded-xl border border-[var(--do-border)] bg-transparent px-3 py-3" />
              <input placeholder="Société" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="rounded-xl border border-[var(--do-border)] bg-transparent px-3 py-3" />
              <select required value={form.sourceId} onChange={(e) => setForm({ ...form, sourceId: e.target.value })} className="rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-3">
                <option value="">Source</option>
                {sources.filter((item) => item.active !== false).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
              </select>
              <input type="email" placeholder="E-mail" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-xl border border-[var(--do-border)] bg-transparent px-3 py-3" />
              <input placeholder="Téléphone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-xl border border-[var(--do-border)] bg-transparent px-3 py-3" />
              <textarea placeholder="Besoin / message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="min-h-28 rounded-xl border border-[var(--do-border)] bg-transparent px-3 py-3 sm:col-span-2" />
            </div>
            <p className="mt-3 text-xs text-[var(--do-muted)]">Au moins un e-mail ou un téléphone est nécessaire pour passer automatiquement à « À affecter ».</p>
            <div className="mt-6 flex justify-end gap-3">
              <button type="button" className="btn-secondary px-5 py-3" onClick={() => setModalOpen(false)} disabled={saving}>Annuler</button>
              <button type="submit" className="btn-primary px-5 py-3" disabled={saving}>{saving ? "Création…" : "Créer"}</button>
            </div>
          </form>
        </div>
      ) : null}
    </ActaStandardPage>
  );
}
